<script setup lang="ts">
import {computed, ref} from "vue";
import dayjs, {Dayjs} from "dayjs";
import "dayjs/locale/fr";
import LegacyAccess from "@/core/interfaces/Access";
import {StaffingTarget} from "@/core/interfaces/TeamPlanning";
import {formatTime} from "@/team/planning";
import {MemberDay, dayShortfalls} from "@/planning/lib/weekPlanning";
import {Check, CircleAlert, CircleCheck, ClipboardList, Clock, X} from "@lucide/vue";

const {members, resolveDay, targets} = defineProps<{
    members: LegacyAccess[];
    resolveDay: (member: LegacyAccess, date: Dayjs) => MemberDay;
    targets: StaffingTarget[];
}>();

const emits = defineEmits<{ (event: 'shift-slot', date: Dayjs): void }>();

interface Issue {
    key: string;
    date: Dayjs;
    label: string;
    detail: string;
}

/** "Marquer traité" only silences a tag for the session: the shortfall
 * itself lives in the data and reappears on reload if still real. */
const dismissed = ref<Set<string>>(new Set());
const openIssue = ref<Issue | null>(null);

const issues = computed<Issue[]>(() => {
    const today = dayjs().locale('fr').startOf('day');

    return Array.from({length: 3}, (_, offset) => today.add(offset, 'day'))
        .flatMap(date => {
            const days = members.map(member => resolveDay(member, date));

            return dayShortfalls(days, targets, date.day()).map(shortfall => ({
                key: `${date.format('YYYY-MM-DD')}-${shortfall.target.id}`,
                date,
                label: `${date.format('ddd DD')} · ${formatTime(shortfall.target.starts_at)}–${formatTime(shortfall.target.ends_at)} · −${shortfall.missing}`,
                detail: `Il manque ${shortfall.missing} personne${shortfall.missing > 1 ? 's' : ''} sur la tranche `
                    + `${formatTime(shortfall.target.starts_at)}–${formatTime(shortfall.target.ends_at)} `
                    + `(cible : ${shortfall.target.headcount}).`,
            }));
        })
        .filter(issue => !dismissed.value.has(issue.key));
});

const resolve = (issue: Issue) => {
    dismissed.value = new Set([...dismissed.value, issue.key]);
    openIssue.value = null;
};
</script>

<template>
  <div class="mb-3.5 rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
    <div class="mb-2.5 flex items-center gap-2.5">
      <ClipboardList class="size-4.5 text-primary" />
      <div class="flex-1">
        <div class="text-sm font-semibold">
          Points de vigilance — 3 prochains jours
        </div>
        <div class="text-[10.5px] text-gray-400">
          Tranches horaires où l'effectif ne couvre pas la cible · cliquez un tag pour traiter
        </div>
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-1.5">
      <button
        v-for="issue in issues"
        :key="issue.key"
        class="flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[10.5px] font-semibold capitalize transition-colors"
        :class="openIssue?.key === issue.key
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400'"
        @click="openIssue = openIssue?.key === issue.key ? null : issue"
      >
        <CircleAlert class="size-3" />
        {{ issue.label }}
      </button>
      <span v-if="!issues.length" class="flex items-center gap-1.5 text-[10.5px] font-semibold text-green-700">
        <CircleCheck class="size-3.5" />
        Aucun souci sur les 3 prochains jours
      </span>
    </div>

    <div v-if="openIssue" class="mt-2.5 rounded-lg border border-primary/20 bg-primary/5 px-3.5 py-3">
      <div class="mb-1 flex items-center gap-2">
        <CircleAlert class="size-4 text-primary" />
        <div class="flex-1 text-xs font-bold capitalize">
          {{ openIssue.date.format('dddd DD MMMM') }}
        </div>
        <button class="text-gray-400 hover:text-gray-600" @click="openIssue = null">
          <X class="size-3.5" />
        </button>
      </div>
      <p class="mb-2.5 text-[11px] leading-relaxed text-primary/90">
        {{ openIssue.detail }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          class="flex h-8 items-center gap-1.5 rounded-lg border border-primary bg-white px-3 text-[11px] font-semibold text-primary hover:bg-primary/5"
          @click="emits('shift-slot', openIssue.date); openIssue = null"
        >
          <Clock class="size-3.5" />
          Décaler une plage
        </button>
        <button
          class="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[11px] font-semibold text-gray-700 hover:border-gray-300"
          @click="resolve(openIssue)"
        >
          <Check class="size-3.5" />
          Marquer traité
        </button>
      </div>
    </div>
  </div>
</template>
